import React, { useEffect, useState } from "react"
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Heading,
  Flex,
  Text,
  LinkBox,
  LinkOverlay,
  Button,
  useToast,
  Link,
} from "@chakra-ui/react"
import Icon from "../Icon/Icon"
import { useGiftRegistryContext } from "@app/providers/giftRegistry"
import { useGiftRegistry } from "@hooks/useGiftRegistry"

import { useCustomerContext } from "@providers/customer"
import { colors } from "@app/theme/colors"

interface GiftRegistryDrawerDesktopProps {
  quantity: any
  grProductData: any
  setShowPasswordDrawer: any
  setGiftRegistryId: number
}

const GiftRegistryDrawerDesktop: React.FC<GiftRegistryDrawerDesktopProps> = ({
  quantity,
  grProductData,
  setShowPasswordDrawer,
  setGiftRegistryId,
}) => {
  const { customer } = useCustomerContext()
  const toast = useToast()
  const toastIdRef = React.useRef()

  const { state, setVal, getRegistries, saveProductToRegistry } = useGiftRegistryContext()
  const { registryListCustomSort, registryActive } = useGiftRegistry()
  const [selectedRegistryId, setSelectedRegistryId] = useState("")
  const [selectedRegistryName, setSelectedRegistryName] = useState("")

  function closeToast() {
    if (toastIdRef.current) {
      toast.close(toastIdRef.current)
    }
  }

  const handleSaveProductToRegistry = async quantity => {
    const registryData = state?.dbRegistries?.find(registry => registry?.Id === selectedRegistryId)
    await saveProductToRegistry(selectedRegistryId, quantity, grProductData)
    setVal("drawers.addToRegistry", false)
    toastIdRef.current = toast({
      position: "bottom",
      duration: 5000,
      isClosable: true,
      containerStyle: {
        maxWidth: "648px",
        maxHeight: "48px",
        width: "648px",
      },
      render: () => (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          bg="#479847"
          width="100%"
          padding="12px 16px"
          borderRadius="60px"
        >
          <Box display="flex" alignItems="center">
            <Icon cursor="pointer" onClick={() => closeToast()} ml="auto" name="tick" size="18px" color="white" />

            <Text ml="10px" color="white" fontSize="16px" fontWeight="400" lineHeight="24px" display="flex">
              Successfully added to {selectedRegistryName}.
              <Link
                onClick={e => {
                  e.stopPropagation()
                  registryData?.settings?.isPasswordProtected == true
                    ? (setShowPasswordDrawer(true), setGiftRegistryId(selectedRegistryId))
                    : (window.location.href = `/gift-registry/view?id=${selectedRegistryId}`)
                }}
                textDecoration="underline"
                color="white"
                fontSize="16px"
                fontWeight="400"
                lineHeight="24px"
                ml="3.5px"
              >
                View
              </Link>
            </Text>
          </Box>
          <Icon cursor="pointer" onClick={() => closeToast()} ml="auto" name="close" size="18px" color="white" />
        </Box>
      ),
    })
  }
  useEffect(() => {
    if (customer && state?.dbRegistries?.length === 0) {
      getRegistries()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer])

  // Sort the items using the customSort function
  if (state?.dbRegistries?.length > 0) {
    state?.dbRegistries.sort(registryListCustomSort)
  }

  return (
    <Drawer
      placement="right"
      isOpen={state?.drawers?.addToRegistry}
      onClose={() => {
        setVal("drawers.addToRegistry", false)
      }}
    >
      <DrawerOverlay></DrawerOverlay>
      <DrawerContent maxW="484px">
        <DrawerBody p={0}>
          <Box h="100%" pl="37px" pt={2} pr={1}>
            <Flex h="100%" flexDirection="column">
              <Flex alignItems="center">
                <Box fontSize="18px" fontWeight="400" lineHeight="27px">
                  Add to Registry
                </Box>
                <Icon cursor="pointer" onClick={() => setVal("drawers.addToRegistry", false)} ml="auto" name="close" size="21px" />
              </Flex>
              <Flex flexGrow={1} flexDirection={"column"} justifyContent="center" mt="151px">
                <Heading as="h2" fontSize="42px" size="2xl" mb="55px">
                  Which Registry?
                </Heading>
                <Flex flexWrap="wrap">
                  {state?.dbRegistries?.map((registry: any, idx: number) => {
                    if (registryActive(registry?.expiryDate)) {
                      return (
                        <Flex
                          onClick={() => {
                            setSelectedRegistryId(registry?.Id)
                            setSelectedRegistryName(registry?.registryName)
                          }}
                          cursor="pointer"
                          mr="16px"
                          mb="8px"
                          key={idx}
                          flexDirection="column"
                        >
                          <Box
                            height="189px"
                            width="189px"
                            borderRadius={"50%"}
                            position={`relative`}
                            bg="#D3D1CF"
                            border={`${selectedRegistryId === registry?.Id ? "3px" : "3px"} solid ${
                              selectedRegistryId === registry?.Id ? "#38A1A8" : "#D3D1CF"
                            }`}
                          >
                            {registry.imageURL && registry.imageURL !== " " && (
                              <img
                                style={{
                                  height: "183px",
                                  width: "183px",
                                  objectFit: "cover",
                                  borderRadius: `50%`,
                                }}
                                src={registry.imageURL}
                              ></img>
                            )}
                          </Box>
                          <Text mx="auto" mt="8px" maxWidth="180px" textAlign="center">
                            {registry.registryName}
                          </Text>
                        </Flex>
                      )
                    }
                  })}
                  <LinkBox>
                    <Flex flexDirection="column" cursor="pointer">
                      <Flex
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="10px"
                        w="189px"
                        h="189px"
                        border={`1px solid #D3D1CF`}
                      >
                        <LinkOverlay href="/account/gift-registry/create">
                          <Icon name="plus" size="21px" color="#4D4A47" />
                        </LinkOverlay>
                      </Flex>
                      <Text mx="auto" mt="8px" color={colors.text.secondary}>
                        Create New
                      </Text>
                    </Flex>
                  </LinkBox>
                </Flex>
                <Box display="flex" mt="60px" mb="10px">
                  <Button
                    width="100%"
                    maxWidth="393px"
                    variant="green"
                    onClick={() => {
                      handleSaveProductToRegistry(quantity)
                    }}
                    disabled={state?.dbRegistries?.length === 0 || selectedRegistryId === ""}
                  >
                    ADD TO REGISTRY
                  </Button>
                </Box>
              </Flex>
            </Flex>
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export default React.memo(GiftRegistryDrawerDesktop)
